import React, { createContext, useContext, useEffect, useState, ReactNode, useRef, useCallback, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { trackBehaviorAndAchievements } from '@/utils/behaviorTracking';
import { logger } from '@/utils/logger';
import { StorageKeys, sessionStorage as storage } from '@/services/storage';
import { clearPaymentFlags } from '@/services/paymentService';
import { authCache } from '@/utils/authCache';
import { retryAuthOperation } from '@/utils/authRetry';
import { authCircuitBreaker } from '@/utils/authCircuitBreaker';

// Debounce helper to prevent duplicate API calls
function debounce<T extends (...args: any[]) => any>(func: T, delay: number): T {
  let timeoutId: NodeJS.Timeout;
  return ((...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  }) as T;
}

interface Profile {
  id: string;
  email: string | null;
  full_name: string | null;
  display_name: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  role: 'admin' | 'user' | null;
  isAdmin: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any; requiresEmailConfirmation?: boolean }>;
  signOut: () => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<{ success: boolean; error?: any }>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: any }>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [role, setRole] = useState<'admin' | 'user' | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const heartbeatInterval = useRef<NodeJS.Timeout | null>(null);
  const lastProcessedEvent = useRef<string | null>(null);
  const profileFetchTimestamp = useRef<Record<string, number>>({});

  // Session tracking functions
  const startHeartbeat = (sessionId: string) => {
    stopHeartbeat();

    heartbeatInterval.current = setInterval(async () => {
      try {
        const { error } = await supabase.functions.invoke('session-heartbeat', {
          body: { session_id: sessionId }
        });
        
        if (error?.message?.includes('Authentication') || error?.message?.includes('401')) {
          logger.log('[Heartbeat] Authentication failed, stopping heartbeat');
          stopHeartbeat();
        }
      } catch (error) {
        logger.log('[Heartbeat] Error, stopping heartbeat:', error?.message);
        stopHeartbeat();
      }
    }, 3600000);
  };

  const stopHeartbeat = () => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
    }
  };

  const trackSession = async (event: string, session: Session) => {
    try {
      const sessionId = session.access_token.substring(0, 20);
      await supabase.functions.invoke('track-session', {
        body: {
          event,
          session_id: sessionId,
          user_agent: navigator.userAgent,
          device_type: /mobile/i.test(navigator.userAgent) ? 'mobile' : 
                      /tablet|ipad/i.test(navigator.userAgent) ? 'tablet' : 'desktop',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      });

      if (event === 'login' || event === 'signup') {
        startHeartbeat(sessionId);
      }
    } catch (error) {
      logger.error('Session tracking error:', error);
    }
  };

  // Debounced fetch to prevent duplicate API calls
  const fetchProfile = useCallback(async (userId: string) => {
    // Prevent duplicate fetches within 500ms
    const now = Date.now();
    if (profileFetchTimestamp.current[userId] && now - profileFetchTimestamp.current[userId] < 500) {
      logger.log('[fetchProfile] Skipping duplicate fetch for:', userId);
      return null;
    }
    profileFetchTimestamp.current[userId] = now;

    logger.log('[fetchProfile] Starting fetch for user:', userId);
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      logger.error('[fetchProfile] Error:', error);
      return null;
    }

    logger.log('[fetchProfile] Success:', data?.id);
    return data as Profile;
  }, []);

  const fetchRole = useCallback(async (userId: string) => {
    logger.log('[fetchRole] Starting fetch for user:', userId);
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .single();

    if (error) {
      logger.log('[fetchRole] No role found, defaulting to user');
      return 'user';
    }

    logger.log('[fetchRole] Success:', data.role);
    return data.role as 'admin' | 'user';
  }, []);

  const refreshProfile = async () => {
    if (!user?.id) return;

    const profileData = await fetchProfile(user.id);
    if (profileData) {
      setProfile(profileData);
    }
  };

  useEffect(() => {
    let loadingTimeout: NodeJS.Timeout;

    const initAuth = async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        
        if (initialSession?.user) {
          logger.log('Auth state changed: INITIAL_SESSION');
          setSession(initialSession);
          setUser(initialSession.user);

          setTimeout(async () => {
            const [profileData, roleData] = await Promise.all([
              fetchProfile(initialSession.user.id),
              fetchRole(initialSession.user.id)
            ]);

            if (profileData) {
              setProfile(profileData);
            }
            
            setRole(roleData);
            setIsLoading(false);
          }, 0);
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        logger.error('[AuthContext] Init error:', error);
        setIsLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      // Prevent duplicate processing of same event
      const eventKey = `${event}-${newSession?.user?.id || 'none'}-${Date.now()}`;
      if (lastProcessedEvent.current === eventKey) {
        logger.log('[AuthContext] Skipping duplicate event:', event);
        return;
      }
      lastProcessedEvent.current = eventKey;
      
      logger.log('Auth state changed:', event);

      // Handle token refresh failures
      if (event === 'TOKEN_REFRESHED' && !newSession) {
        logger.error('[AuthContext] Token refresh failed - clearing session');
        authCircuitBreaker.recordFailure({ status: 503, message: 'Token refresh failed' });
        
        if (authCircuitBreaker.shouldBlock()) {
          setUser(null);
          setSession(null);
          setProfile(null);
          setRole(null);
          stopHeartbeat();
          authCache.clear();
          setIsLoading(false);
          
          toast({
            title: 'Connection Issue',
            description: 'Service temporarily unavailable. Please refresh and try again.',
            variant: 'destructive',
          });
        }
        return;
      }

      if (event === 'SIGNED_OUT') {
        setUser(null);
        setSession(null);
        setProfile(null);
        setRole(null);
        stopHeartbeat();
        authCache.clear();
        authCircuitBreaker.reset();
        setIsLoading(false);
        return;
      }

      if (event === 'SIGNED_IN' && newSession?.user) {
        logger.log('[AuthContext] SIGNED_IN event - User ID:', newSession.user.id);
        authCircuitBreaker.recordSuccess();
        setSession(newSession);
        setUser(newSession.user);

        setTimeout(async () => {
          try {
            const [profileData, roleData] = await Promise.all([
              fetchProfile(newSession.user.id),
              fetchRole(newSession.user.id)
            ]);

            if (profileData) {
              setProfile(profileData);
            }
            
            setRole(roleData);
            
            logger.log('[AuthContext] Profile and role set:', {
              profileId: profileData?.id,
              userRole: roleData
            });

            setIsLoading(false);
          } catch (error) {
            logger.error('[AuthContext] Error fetching profile/role:', error);
            setIsLoading(false);
          }
        }, 0);
      }
    });

    initAuth();

    loadingTimeout = setTimeout(() => {
      if (isLoading) {
        logger.warn('[AuthContext] Loading timeout reached, forcing load complete');
        setIsLoading(false);
      }
    }, 10000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(loadingTimeout);
      stopHeartbeat();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    return await retryAuthOperation(async () => {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        toast({
          title: 'Login Failed',
          description: error.message,
          variant: 'destructive',
        });
        return { error };
      }

      if (data.session) {
        await trackSession('login', data.session);
        await trackBehaviorAndAchievements(data.user.id, 'user_login', {
          login_method: 'email',
          login_time: new Date().toISOString()
        });
      }

      toast({ title: 'Welcome back!', description: 'You have successfully logged in.' });
      return { error: null };
    });
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    return await retryAuthOperation(async () => {
      const redirectUrl = `${window.location.origin}/`;
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: { full_name: fullName }
        }
      });

      if (error) {
        toast({
          title: 'Signup Failed',
          description: error.message,
          variant: 'destructive',
        });
        return { error };
      }

      if (data.session) {
        await trackSession('signup', data.session);
      }

      const requiresEmailConfirmation = !data.session && !!data.user;

      if (requiresEmailConfirmation) {
        toast({
          title: 'Check Your Email',
          description: 'We sent you a confirmation link. Please check your inbox.',
        });
      } else {
        toast({
          title: 'Account Created!',
          description: 'Welcome to Command AI.',
        });
      }

      return { error: null, requiresEmailConfirmation };
    });
  };

  const signOut = async () => {
    try {
      stopHeartbeat();
      
      if (session) {
        const sessionId = session.access_token.substring(0, 20);
        await supabase.functions.invoke('track-session', {
          body: {
            event: 'logout',
            session_id: sessionId
          }
        });
      }

      clearPaymentFlags();
      authCache.clear();
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        toast({
          title: 'Logout Error',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }

      setUser(null);
      setSession(null);
      setProfile(null);
      setRole(null);

      toast({ title: 'Logged Out', description: 'You have been successfully logged out.' });
    } catch (error: any) {
      logger.error('[SignOut] Error:', error);
      toast({
        title: 'Logout Error',
        description: error.message || 'An error occurred during logout',
        variant: 'destructive',
      });
    }
  };

  const resendVerificationEmail = async (email: string) => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });

      if (error) {
        toast({
          title: 'Resend Failed',
          description: error.message,
          variant: 'destructive',
        });
        return { success: false, error };
      }

      toast({
        title: 'Email Sent',
        description: 'Verification email has been resent. Please check your inbox.',
      });

      return { success: true };
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to resend verification email',
        variant: 'destructive',
      });
      return { success: false, error };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        toast({
          title: 'Reset Failed',
          description: error.message,
          variant: 'destructive',
        });
        return { success: false, error };
      }

      toast({
        title: 'Check Your Email',
        description: 'Password reset instructions have been sent to your email.',
      });

      return { success: true };
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send reset email',
        variant: 'destructive',
      });
      return { success: false, error };
    }
  };

  const value = {
    user,
    session,
    profile,
    role,
    isAdmin: role === 'admin',
    isLoading,
    signIn,
    signUp,
    signOut,
    resendVerificationEmail,
    resetPassword,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
